import { FollowableUser, NewsType, Paper } from 'js/social/shared/types';

// Should be in sync with backend methods:
// app/lib/liswp_sidebar.rb#get_recommendations
// app/lib/abstract_sidebar.rb#serialize_author
// app/lib/abstract_sidebar.rb#serialize_authors
export type User = {
  id: number;
  displayName: string;
  university?: string;
  position?: string;
  url?: string;
  photo?: string;

  // Popover props:
  followers?: number;
  following?: boolean; // If the current user is following this author
  workCount?: number;
};

// The author type returned for the 'About the authors' section at the top.
//
// Should be in sync with backend method:
// app/lib/liswp_sidebar.rb#get_authors
export type Author = User & {
  bio: string;
  papers: number;
  // Does the author want their badge shown?
  show_premium_badge: boolean;
};

// Should be in sync with backend method:
// app/lib/liswp_sidebar.rb#get_author_works
export type AuthorWorks = {
  author: string; // Author's display name
  count: number; // The number of works the author has minus 1 (for the current work being viewed)
  url: string; // URL to the author's profile
  works: Work[]; // Will only ever be one work
};

// Should be in sync with backend method:
// app/lib/abstract_sidebar.rb#serialize_works
export type Work = {
  // Used for bulk download modal
  attachments: Array<{
    url: string;
    id: number;
  }>;
  authors: User[];
  bookmarkId?: number;
  id: number;
  image: string;
  internal_url: string;
  // Used for bulk download modal
  slug: string;
  title: string;
  // for backwards compatibility, 'Work' is default
  type?: 'Work' | 'OpenalexWork';
};

export type Recommendation = {
  // A user can leave a recommendation without a comment
  comment?: string;
  // DateTime the user left the recommendation
  date: string;
  user: User;
};

// Should be in sync with backend method:
// app/lib/abstract_sidebar.rb#get_research_interests
export type ResearchInterest = {
  id: number;
  // How many followers the research interest has
  count: number;
  // Whether the user is currently following the research interest
  following?: boolean;
  name: string;
  url: string;
};

export type BulkFollowCardData = {
  topic: {
    name: string;
    score: number;
  };
  users: FollowableUser[];
};

export type CommentInfo = {
  numComments: number;
  newsType?: NewsType;
  newsId?: number | string;
  author: FollowableUser;
  socialWork: Paper;
};

export enum SidebarSection {
  Authors = 'authors',
  AuthorWorks = 'authorWorks',
  Readers = 'bookmarkers',
  Recommendations = 'recommendations',
  RelatedWorks = 'relatedWorks',
  ResearchInterests = 'researchInterests',
  Comments = 'comments',
}

// Should be in sync with app/lib/liswp_sidebar.rb#get_json
export type SidebarData = {
  [SidebarSection.Authors]?: Author[];
  [SidebarSection.AuthorWorks]?: {
    count: number;
    url: string;
    works: Work[];
  };
  [SidebarSection.Readers]?: boolean;
  [SidebarSection.Recommendations]?: Recommendation[];
  [SidebarSection.RelatedWorks]?: Work[];
  [SidebarSection.ResearchInterests]?: ResearchInterest[];
  [SidebarSection.Comments]?: CommentInfo;
};
